import { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  AlloyButton,
  Dropdown,
  Input,
  Validator,
  Radio,
  RadioGroup
} from 'cat-ecommerce-alloy';
import {
  replaceTokensInString,
  isEmpty,
  doublebyteChecker,
  regExpressionChecker
} from '@app/utils';
import Field from '@app/components/common/form/Field';
import styles from './AdressFormFields.module.scss';
import {
  ADDRESS_TYPES,
  ADDRESS_TYPE_LABELS,
  INVALID_EMAIL_DOMAINS
} from '@app/components/common/address/constants';
import {
  COMMON_INVALID_CHARS,
  ADD_NEW_ADDRESS_VALIDATOR_NEW_PARAMETERS,
  JAPANESE_LANGUAGE_CODE,
  PAGE_BREAKPOINTS
} from '@app/constants/commonConstants';
import { PCC_PUD_TITLE_UPDATES } from '@app/constants/featureFlags';
import PhoneNumberInput from './PhoneNumberInput';
import Conditional from '@app/components/common/Conditional';
import { useZipCodeService, useBreakpoint } from '@app/hooks';
import { useFormikContext } from 'formik';
import { useSelector } from 'react-redux';
import { isSummaryAndPaymentPage } from '@app/utils/browserUtils';
import { DrawerDropdown } from '@app/components/common/DrawerDropdown';
import cx from 'classnames';

import { useMyAccountLinks } from '@app/components/pages/account/hooks';
const DEFAULT_ADDRESS_LIMIT = '40';

const AddressFormFields = ({
  countryData = [],
  addressLimit = DEFAULT_ADDRESS_LIMIT,
  fieldPrefix = '',
  isAddressTypeEditable = false,
  isAddressNameEditable = false,
  menuSize,
  nickName,
  addressType,
  onCountryChange,
  onEmailChange,
  onZipCodeBlur,
  requiredFields,
  selectedCountry,
  selectedState,
  showAddressLine2 = false,
  showAddressName = false,
  showAddressType = false,
  showEmail = false,
  showName = false,
  showPhoneNumber = false,
  showPhoneNumberWithVerification = false, //if true, it will change the default phone number component to one which verifies with googleliphonenumber library
  autoCompleteOnZipcode = false,
  forcePhoneRequired = false,
  forceLine1Required = false,
  forceEmailRequired = false,
  validateEmailDomains = false,
  isDeepLinkFlow = false,
  showNeighborhood = true, // Default is to true. Neigborhood field is hidden only in address book and summary and payment pages.
  handleValueFromSessionStorage = false,
  valuefromSessionStorage = {},
  isDealerUser = false,
  profileAddressId = '',
  labelClassName = '',
  showMobileDrawerFlag = false
}) => {
  const [t] = useTranslation();
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);
  const { getCityState } = useZipCodeService();
  const { values, setFieldValue, setFieldError } = useFormikContext();
  const INVALID_CHAR_MSG = t('ADD_INV_CHAR_NEW_FIELD_WARNING');
  const FIELD_REQUIRED_MSG = t('1626_FIELD_REQUIRED_ERROR');
  const inValidZipCodeMessage = t('INVALID_ZIP_CODE_FORMAT');
  const INVALID_EMAIL_MESSAGE = t('INVALID_EMAIL_FORMAT');
  const normalizedFieldPrefix = fieldPrefix ? `${fieldPrefix}.` : '';
  const parsedAddressLimit = parseInt(addressLimit ?? DEFAULT_ADDRESS_LIMIT);
  const [isZipCodeSupported, setIsZipCodeSupported] = useState(true);
  const { staticLinks } = useMyAccountLinks();
  const personalInfoUrl = staticLinks.PERSONAL_INFO.url;
  const { pathname, search } = window.location;
  const isPersonalInformationPage = personalInfoUrl === pathname + search;
  const getFieldId = useCallback(
    fieldIdentifier => {
      return `${normalizedFieldPrefix}${fieldIdentifier}`;
    },
    [normalizedFieldPrefix]
  );
  const allAddress = useSelector(state => state?.checkout?.addresses?.options);
  const profileAddressNickName = useCallback(() => {
    return allAddress && !isEmpty(profileAddressId)
      ? allAddress.find(({ addressId }) => addressId === profileAddressId)
          ?.nickName
      : {};
  }, [allAddress, profileAddressId]);

  const isAllFieldAvailNonDealer =
    isSummaryAndPaymentPage() &&
    !isDealerUser &&
    !isEmpty(profileAddressNickName);
  const isFieldRequired = useCallback(
    fieldName => {
      let isRequiredField =
        requiredFields && requiredFields.includes(fieldName);
      if (fieldName === 'zipCode' && !isZipCodeSupported) {
        isRequiredField = false;
      }
      return isRequiredField;
    },
    [isZipCodeSupported, requiredFields]
  );
  const isAddress2Required = isFieldRequired('address2');
  const [showAddress2, setShowAddress2] = useState(
    isAddress2Required || showAddressLine2
  );
  const showMobileDrawers = showMobileDrawerFlag && !isMD;
  // Neighborhood related options.
  const neighborhoodLabel = useSelector(
    state => state?.dealer.neighborhoodLabel
  );
  const isNeighborhoodRequired = useSelector(
    state => state?.dealer.isNeighborhoodRequired
  );
  const isNeighborhoodEnabled = useSelector(
    state => state?.dealer.isNeighborhoodEnabled
  );
  const countriesHaveNoZipOrState = useSelector(
    state => state?.dealer?.countriesHaveNoZipOrState
  );
  const isDefaultLanguageJapanese = useSelector(
    s => s.dealer.defaultLanguage === JAPANESE_LANGUAGE_CODE
  );
  const isPCC_ShoppingCartSingleByteMessageJapan = useSelector(
    s => s.featureFlag?.PCC_ShoppingCartSingleByteMessageJapan
  );
  const isPCC_SpecialCharactersValidationFlag = useSelector(
    s => s.featureFlag?.PCC_SpecialCharactersValidationFlag
  );

  const pUDTitleUpdatesFlag = useSelector(
    s => s.featureFlag[PCC_PUD_TITLE_UPDATES]
  );
  const validateDoubleByte = useMemo(
    () => isDefaultLanguageJapanese && isPCC_ShoppingCartSingleByteMessageJapan,
    [isDefaultLanguageJapanese, isPCC_ShoppingCartSingleByteMessageJapan]
  );
  const isContrySupportedZipCode = useCallback(
    selectedCountry => {
      let isZipCodeSupported = true;
      const country = selectedCountry
        ? countryData.find(({ displayName }) => displayName === selectedCountry)
        : null;
      if (
        country &&
        country.code &&
        countriesHaveNoZipOrState &&
        countriesHaveNoZipOrState.length
      ) {
        isZipCodeSupported = !countriesHaveNoZipOrState.includes(country.code);
      }
      return isZipCodeSupported;
    },
    [countriesHaveNoZipOrState, countryData]
  );
  useEffect(() => {
    setShowAddress2(isAddress2Required || showAddressLine2);
  }, [isAddress2Required, showAddressLine2]);
  useEffect(() => {
    setIsZipCodeSupported(isContrySupportedZipCode(selectedCountry));
  }, [isContrySupportedZipCode, selectedCountry]);
  // Clears required error in zipCode field if user changes from a supported-zipCode
  // country to an unsupported-zipCode country
  useEffect(() => {
    const isZipCodeRequired = isFieldRequired('zipCode');
    if (!isZipCodeSupported && !isZipCodeRequired) {
      setFieldError(getFieldId('zipCode'), '');
    }
  }, [getFieldId, isFieldRequired, isZipCodeSupported, setFieldError]);
  // the second parameter is undefined because the string uses {1} instead of {0}
  const getDefaultValidator = useCallback(
    (val = '', invalidCharsStr) => {
      const invalidCharErrorStr = `${COMMON_INVALID_CHARS} ${invalidCharsStr ? ',' + invalidCharsStr : ''}`;
      return new Validator(val).isInvalidCharacter(
        replaceTokensInString(INVALID_CHAR_MSG, undefined, invalidCharErrorStr)
      );
    },
    [INVALID_CHAR_MSG]
  );
  const getValidator = useCallback(
    (val, fieldName, displayName, forceRequired) => {
      const hasNewinvalidChars =
        isPCC_SpecialCharactersValidationFlag &&
        ADD_NEW_ADDRESS_VALIDATOR_NEW_PARAMETERS[fieldName]?.invalidCharsStr;
      let validator =
        val !== null
          ? getDefaultValidator(val, hasNewinvalidChars)
          : getDefaultValidator('', hasNewinvalidChars);
      if (validateDoubleByte && doublebyteChecker(val)) {
        return setFieldError(
          getFieldId(fieldName),
          t('ERROR_SINGLE_BYTE_REQUIRED')
        );
      }
      if (isFieldRequired(fieldName) || forceRequired) {
        validator = validator?.required(
          replaceTokensInString(FIELD_REQUIRED_MSG, displayName)
        );
      }
      return validator;
    },
    [
      FIELD_REQUIRED_MSG,
      getDefaultValidator,
      getFieldId,
      isFieldRequired,
      isPCC_SpecialCharactersValidationFlag,
      setFieldError,
      t,
      validateDoubleByte
    ]
  );
  const getInvalidCharsError = useCallback(
    (val, fieldName) => {
      const newInvalidChars =
        ADD_NEW_ADDRESS_VALIDATOR_NEW_PARAMETERS[fieldName];
      if (
        isPCC_SpecialCharactersValidationFlag &&
        newInvalidChars &&
        regExpressionChecker(newInvalidChars.regex, val)
      ) {
        const invalidCharErrorStr = `${COMMON_INVALID_CHARS} ${newInvalidChars.invalidCharsStr ? ',' + newInvalidChars.invalidCharsStr : ''}`;

        if (fieldName === 'email') {
          return INVALID_EMAIL_MESSAGE;
        }
        if (fieldName === 'zipCode' && !isPersonalInformationPage) {
          // and I am not in personal info page
          return inValidZipCodeMessage;
        }

        return replaceTokensInString(
          INVALID_CHAR_MSG,
          undefined,
          invalidCharErrorStr
        );
      }
      return;
    },
    [
      INVALID_CHAR_MSG,
      INVALID_EMAIL_MESSAGE,
      inValidZipCodeMessage,
      isPCC_SpecialCharactersValidationFlag
    ]
  );
  const getFieldValidations = useCallback(
    (val, fieldName, displayName, forceRequired) => {
      let validations = getInvalidCharsError(val, fieldName);
      if (!validations) {
        validations = getValidator(
          val,
          fieldName,
          displayName,
          forceRequired
        )?.getError();
      }
      return validations;
    },
    [getInvalidCharsError, getValidator]
  );
  const isAddressRequiredFlag =
    isFieldRequired('address1') || forceLine1Required;
  const isEmailRequiredFlag = isFieldRequired('email1') || forceEmailRequired;
  const getEmailValidator = useCallback(
    (val, fieldName, displayName) => {
      // The required fields service is sending the name of the email field
      // as 'email1' which doesn't match the form's name 'email'.
      const isEmailRequiredOverride = isEmailRequiredFlag;
      const validations = getFieldValidations(
        val,
        fieldName,
        displayName,
        isEmailRequiredOverride
      );
      return (
        validations ??
        getValidator(val, fieldName, displayName, isEmailRequiredOverride)
          ?.isValidEmail(t('ERROR_INVALIDEMAILFORMAT'))
          ?.getError()
      );
    },
    [getFieldValidations, getValidator, isEmailRequiredFlag, t]
  );
  const getEmailDomainsValidator = useCallback(
    val => {
      let error = null;
      const emailDomain = val?.substring(val.lastIndexOf('@')).toLowerCase();
      if (
        validateEmailDomains &&
        val !== '' &&
        INVALID_EMAIL_DOMAINS.includes(emailDomain)
      ) {
        error = t('REGISTRATION_EMAIL_DOMAIN_ERROR');
      }
      return error;
    },
    [t, validateEmailDomains]
  );
  const isPhoneRequiredFlag = isFieldRequired('phone1') || forcePhoneRequired;
  const getPhoneValidator = useCallback(
    (val, fieldName, displayName) => {
      // The required fields service is sending the name of the phone field
      // as 'phone1' which doesn't match the form's name 'phone'.
      const isPhoneRequiredOverride = isPhoneRequiredFlag;
      const validations = getFieldValidations(
        val,
        fieldName,
        displayName,
        isPhoneRequiredOverride
      );
      return (
        validations ??
        getValidator(val, fieldName, displayName, isPhoneRequiredOverride)
          .isValidPhoneNumber(t('ERROR_INVALIDPHONE'))
          ?.getError()
      );
    },
    [getFieldValidations, getValidator, isPhoneRequiredFlag, t]
  );
  const getStatesInSelectedCountry = useCallback(
    selected => {
      const filtered = countryData.find(
        ({ displayName }) => displayName === selected
      );
      const { states = null } = filtered || {};
      return states;
    },
    [countryData]
  );
  const onBlurZipCode = async e => {
    const error = getValidator(
      //extracted error before validator to send the country & state request when the ZipCode is valid only
      e.target.value,
      'zipCode',
      t('CAT_EMAIL_ZIP')
    )?.getError();
    // Access dynamically to the country's route when it has a preffix
    const getCountryPath = (path, obj) => {
      return path.split('.').reduce((prev, curr) => {
        return prev ? prev[curr] : null;
      }, obj);
    };
    if (autoCompleteOnZipcode && !error) {
      const countryFieldName = getFieldId('country');
      const { city, state } = await getCityState({
        countryName: getCountryPath(countryFieldName, values),
        zipCode: e.target.value
      });
      !!city && setFieldValue(getFieldId('city'), city, true);
      !!state && setFieldValue(getFieldId('state'), state, true);
    }
    if (onZipCodeBlur && !error) {
      onZipCodeBlur();
    }
  };
  const countries = countryData.map(({ displayName }) => displayName);
  const statesOrProvinces = getStatesInSelectedCountry(selectedCountry);

  const getNameFields = useCallback(
    () => (
      <>
        <Field
          type="text"
          as={Input}
          name={getFieldId('firstName')}
          id={getFieldId('firstName')}
          label={t('CAT_EMAIL_FIRSTNAME')}
          validator={val =>
            getFieldValidations(
              val,
              'firstName',
              t('CAT_EMAIL_FIRSTNAME'),
              true
            )
          }
          labelClassName={cx(labelClassName, {
            'text-capitalize': pUDTitleUpdatesFlag
          })}
          constrainBytes={20}
          maxLength="20"
          showRequired
          data-cs-mask
        />
        <Field
          type="text"
          as={Input}
          name={getFieldId('lastName')}
          id={getFieldId('lastName')}
          label={t('CAT_EMAIL_LASTNAME')}
          validator={val =>
            getFieldValidations(val, 'lastName', t('CAT_EMAIL_LASTNAME'), true)
          }
          labelClassName={cx(labelClassName, {
            'text-capitalize': pUDTitleUpdatesFlag
          })}
          constrainBytes={20}
          maxLength="20"
          showRequired
          data-cs-mask
        />
      </>
    ),
    [getFieldId, getFieldValidations, labelClassName, t]
  );

  const getNicknameValidator = useCallback(
    val => {
      if (isAllFieldAvailNonDealer && val === profileAddressNickName()) {
        return t('ERROR_NICKNAME_ALREDY_EXIST');
      }

      return getFieldValidations(val, 'nickName', t('ADDRESS_NAME'), true);
    },
    [getFieldValidations, isAllFieldAvailNonDealer, profileAddressNickName, t]
  );

  const getNicknameField = useCallback(
    editable =>
      editable ? (
        <Field
          type="text"
          as={Input}
          name={getFieldId('nickName')}
          id={getFieldId('nickName')}
          label={t('ADDRESS_NAME')}
          validator={getNicknameValidator}
          labelClassName={cx(labelClassName, {
            'text-capitalize': pUDTitleUpdatesFlag
          })}
          constrainBytes={140}
          maxLength="140"
          showRequired
          data-cs-mask
        />
      ) : (
        <div>
          <p
            className={cx('exclude-global input__label', labelClassName, {
              'text-capitalize': pUDTitleUpdatesFlag
            })}
          >
            {t('ADDRESS_NAME')}
          </p>
          <p className="exclude-global mt-2">{nickName}</p>
        </div>
      ),
    [getFieldId, getNicknameValidator, labelClassName, nickName, t]
  );

  const getStatesAsDropdown = useCallback(() => {
    const stateDisplayOptions = statesOrProvinces.map(
      ({ displayName }) => displayName
    );
    return (
      <div
        className={cx({
          [styles['address-dropdown__capitalize']]: pUDTitleUpdatesFlag
        })}
      >
        {showMobileDrawers ? (
          <DrawerDropdown
            name={getFieldId('state')}
            id={getFieldId('state')}
            items={stateDisplayOptions}
            label={t('CAT_EMAIL_STATE')}
            validator={val =>
              getFieldValidations(val, 'state', t('CAT_EMAIL_STATE'))
            }
            onChange={value => {
              setFieldValue(getFieldId('state'), value);
              setFieldError(getFieldId('state'), '');
            }}
            selected={selectedState}
            showRequired={isFieldRequired('state')}
            headerVariant="regular"
            orientation="side"
            verticalMaxHeight="large"
            showBackButton
            withSearch
            searchPlaceHolder={t('SEARCH_STATE_PROVINCE')}
          />
        ) : (
          <Field
            type="dropdown"
            as={Dropdown}
            items={stateDisplayOptions}
            menuSize={menuSize}
            name={getFieldId('state')}
            id={getFieldId('state')}
            label={t('CAT_EMAIL_STATE')}
            validator={val =>
              getFieldValidations(val, 'state', t('CAT_EMAIL_STATE'))
            }
            labelClassName={labelClassName}
            showRequired={isFieldRequired('state')}
            showOptional={!isFieldRequired('state')}
            data-cs-mask
          />
        )}
      </div>
    );
  }, [
    getFieldId,
    getFieldValidations,
    isFieldRequired,
    labelClassName,
    menuSize,
    statesOrProvinces,
    showMobileDrawers,
    selectedState,
    t
  ]);
  const getStatesAsInput = useCallback(
    () => (
      <Field
        type="text"
        as={Input}
        name={getFieldId('state')}
        id={getFieldId('state')}
        label={t('CAT_EMAIL_STATE')}
        validator={val =>
          getFieldValidations(val, 'state', t('CAT_EMAIL_STATE'))
        }
        labelClassName={cx(labelClassName, {
          'text-capitalize': pUDTitleUpdatesFlag
        })}
        constrainBytes={40}
        maxLength="40"
        showRequired={isFieldRequired('state')}
        showOptional={!isFieldRequired('state')}
        data-cs-mask
      />
    ),
    [getFieldId, getFieldValidations, isFieldRequired, labelClassName, t]
  );
  const getAddressTypeText = useCallback(
    addressType => {
      return {
        [ADDRESS_TYPE_LABELS.shippingAndBilling]: t('AB_SBADDR'),
        [ADDRESS_TYPE_LABELS.shipping]: t('SHIPPING_ADDRESS2'),
        [ADDRESS_TYPE_LABELS.billing]: t('BILLINGADDRESS')
      }[addressType];
    },
    [t]
  );
  const getAddressType = useCallback(
    editable =>
      editable ? (
        <RadioGroup
          label={t('ADDRESS_TYPE_LABEL')}
          showRequired
          className="d-md-flex"
          labelClassName={cx(labelClassName, {
            'text-capitalize': pUDTitleUpdatesFlag
          })}
          validator={val =>
            getValidator(
              val,
              'addressType',
              t('ADDRESS_TYPE_LABEL'),
              true
            )?.getError()
          }
        >
          <Field
            className="me-5"
            type="radio"
            as={Radio}
            name="addressType"
            id="addressType1"
            label={t('AB_SBADDR')}
            value={ADDRESS_TYPES.shippingAndBilling}
            labelClassName={cx(labelClassName, {
              'text-capitalize': pUDTitleUpdatesFlag
            })}
          />
          <Field
            className="me-5"
            type="radio"
            as={Radio}
            name="addressType"
            id="addressType2"
            label={t('BILLINGADDRESS')}
            value={ADDRESS_TYPES.billing}
            labelClassName={cx(labelClassName, {
              'text-capitalize': pUDTitleUpdatesFlag
            })}
          />
          <Field
            className="me-5"
            type="radio"
            as={Radio}
            name="addressType"
            id="addressType3"
            label={t('SHIPPING_ADDRESS2')}
            value={ADDRESS_TYPES.shipping}
            labelClassName={cx(labelClassName, {
              'text-capitalize': pUDTitleUpdatesFlag
            })}
          />
        </RadioGroup>
      ) : (
        <div>
          <p
            className={cx('exclude-global input__label', labelClassName, {
              'text-capitalize': pUDTitleUpdatesFlag
            })}
          >
            {t('ADDRESS_TYPE_LABEL')}
          </p>
          <p className="exclude-global mt-2">
            {getAddressTypeText(addressType)}
          </p>
        </div>
      ),
    [addressType, getAddressTypeText, getValidator, labelClassName, t]
  );
  return (
    <>
      {showAddressName && getNicknameField(isAddressNameEditable)}
      {showAddressType && getAddressType(isAddressTypeEditable)}
      {showName && getNameFields()}
      <div
        className={cx({
          [styles['address-dropdown__capitalize']]: pUDTitleUpdatesFlag
        })}
      >
        {showMobileDrawers ? (
          <DrawerDropdown
            name={getFieldId('country')}
            id={getFieldId('country')}
            items={countries}
            label={t('COUNTRY_REGION')}
            onChange={value => {
              setFieldValue(getFieldId('country'), value);
              setFieldError(getFieldId('country'), '');
              onCountryChange();
            }}
            selected={selectedCountry}
            validator={val =>
              getValidator(
                val,
                'country',
                t('COUNTRY_REGION'),
                true
              )?.getError()
            }
            showRequired
            headerVariant="regular"
            orientation="side"
            verticalMaxHeight="large"
            showBackButton
            withSearch
            searchPlaceHolder={t('SEARCH_COUNTRY_REGION')}
          />
        ) : (
          <Field
            type="dropdown"
            as={Dropdown}
            items={countries}
            menuSize={menuSize}
            name={getFieldId('country')}
            id={getFieldId('country')}
            label={t('COUNTRY_REGION')}
            onChange={onCountryChange}
            validator={val =>
              getValidator(
                val,
                'country',
                t('COUNTRY_REGION'),
                true
              )?.getError()
            }
            labelClassName={labelClassName}
            showRequired
            data-cs-mask
          />
        )}
      </div>
      {isNeighborhoodEnabled && showNeighborhood && (
        <Field
          type="text"
          as={Input}
          name="neighborhood"
          id="neighborhood"
          data-testid="neighborhood"
          label={neighborhoodLabel}
          showRequired={isNeighborhoodRequired}
          maxLength="40"
          validator={val =>
            getValidator(
              val,
              'neighborhood',
              neighborhoodLabel,
              isNeighborhoodRequired
            )?.getError()
          }
          labelClassName={cx(labelClassName, {
            'text-capitalize': pUDTitleUpdatesFlag
          })}
          data-cs-mask
        />
      )}
      <Field
        type="text"
        as={Input}
        name={getFieldId('zipCode')}
        id={getFieldId('zipCode')}
        label={t('CAT_EMAIL_ZIP')}
        validator={val =>
          getFieldValidations(val, 'zipCode', t('CAT_EMAIL_ZIP'))
        }
        labelClassName={cx(labelClassName, {
          'text-capitalize': pUDTitleUpdatesFlag
        })}
        onBlur={onBlurZipCode}
        constrainBytes={10}
        maxLength="10"
        showRequired={isFieldRequired('zipCode')}
        showOptional={!isFieldRequired('zipCode')}
        data-cs-mask
      />
      <Field
        type="text"
        as={Input}
        name={getFieldId('address1')}
        id={getFieldId('address1')}
        label={t('CAT_EMAIL_ADDRESS_1')}
        validator={val =>
          getFieldValidations(val, 'address1', t('CAT_EMAIL_ADDRESS_1'))
        }
        labelClassName={cx(labelClassName, {
          'text-capitalize': pUDTitleUpdatesFlag
        })}
        constrainBytes={parsedAddressLimit}
        maxLength={addressLimit ?? DEFAULT_ADDRESS_LIMIT}
        showRequired={isAddressRequiredFlag}
        showOptional={!isAddressRequiredFlag}
        data-cs-mask
      />
      {showAddress2 && (
        <Field
          type="text"
          as={Input}
          name={getFieldId('address2')}
          id={getFieldId('address2')}
          label={t('CAT_EMAIL_ADDRESS_2')}
          validator={val =>
            getFieldValidations(val, 'address2', t('CAT_EMAIL_ADDRESS_2'))
          }
          labelClassName={cx(labelClassName, {
            'text-capitalize': pUDTitleUpdatesFlag
          })}
          constrainBytes={parsedAddressLimit}
          maxLength={addressLimit ?? DEFAULT_ADDRESS_LIMIT}
          showRequired={isAddress2Required}
          showOptional={!isAddress2Required}
          data-cs-mask
        />
      )}
      {!showAddress2 && (
        <AlloyButton
          buttonType="ghost"
          addClass={cx('ps-0 mt-n3 mb-3', labelClassName, {
            'text-capitalize': pUDTitleUpdatesFlag
          })}
          onClick={() => setShowAddress2(true)}
        >
          {t('ADD_ADDRESS_LINE_2')}
        </AlloyButton>
      )}
      <Field
        type="text"
        as={Input}
        name={getFieldId('city')}
        id={getFieldId('city')}
        label={t('CAT_EMAIL_CITY')}
        validator={val => getFieldValidations(val, 'city', t('CAT_EMAIL_CITY'))}
        labelClassName={cx(labelClassName, {
          'text-capitalize': pUDTitleUpdatesFlag
        })}
        constrainBytes={40}
        maxLength="40"
        showRequired={isFieldRequired('city')}
        showOptional={!isFieldRequired('city')}
        data-cs-mask
      />
      {statesOrProvinces && statesOrProvinces.length > 0
        ? getStatesAsDropdown()
        : getStatesAsInput()}
      <Conditional test={showPhoneNumber}>
        {showPhoneNumberWithVerification ? (
          <PhoneNumberInput
            handleValueFromSessionStorage={handleValueFromSessionStorage}
            valuefromSessionStorage={valuefromSessionStorage}
            dropDownName={getFieldId('phoneCode')}
            inputName={getFieldId('phone1')}
            showRequired={isPhoneRequiredFlag}
            labelClassName={
              pUDTitleUpdatesFlag ? 'text-capitalize' : labelClassName
            }
            showMobileDrawerFlag={showMobileDrawers}
          />
        ) : (
          <Field
            type="tel"
            as={Input}
            name={getFieldId('phone')}
            id={getFieldId('phone')}
            label={t('CAT_EMAIL_PHONE1')}
            validator={val =>
              getPhoneValidator(val, 'phone', t('CAT_EMAIL_PHONE1'))
            }
            labelClassName={cx(labelClassName, {
              'text-capitalize': pUDTitleUpdatesFlag
            })}
            noValidate
            constrainBytes={16}
            maxLength="16"
            showRequired={isPhoneRequiredFlag}
            showOptional={!isPhoneRequiredFlag}
            data-cs-mask
          />
        )}
      </Conditional>
      {showEmail && (
        <Field
          type="email"
          as={Input}
          name={getFieldId('email')}
          id={getFieldId('email')}
          label={t('CAT_EMAIL_EMAIL1')}
          disabled={isDeepLinkFlow}
          onChange={onEmailChange}
          validator={val => {
            if (!isDeepLinkFlow) {
              let emailError = null;
              emailError = getEmailValidator(
                val,
                'email',
                t('CAT_EMAIL_EMAIL1')
              );
              if (!emailError) {
                emailError = getEmailDomainsValidator(val);
              }
              return emailError;
            }
          }}
          labelClassName={cx(labelClassName, {
            'text-capitalize': pUDTitleUpdatesFlag
          })}
          constrainBytes={50}
          noValidate
          maxLength="50"
          showRequired={isEmailRequiredFlag}
          showOptional={!isEmailRequiredFlag}
          data-cs-mask
        />
      )}
    </>
  );
};
AddressFormFields.propTypes = {
  addressLimit: PropTypes.string,
  countryData: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string,
      displayName: PropTypes.string.isRequired,
      states: PropTypes.arrayOf(
        PropTypes.shape({
          code: PropTypes.string,
          displayName: PropTypes.string
        })
      )
    })
  ),
  fieldPrefix: PropTypes.string,
  handleValueFromSessionStorage: PropTypes.bool,
  isAddressTypeEditable: PropTypes.bool,
  isAddressNameEditable: PropTypes.bool,
  menuSize: PropTypes.oneOf(['small', 'large']),
  requiredFields: PropTypes.arrayOf(PropTypes.string),
  showAddressLine2: PropTypes.bool,
  showAddressName: PropTypes.bool,
  showEmail: PropTypes.bool,
  showName: PropTypes.bool,
  showPhoneNumber: PropTypes.bool,
  showPhoneNumberWithVerification: PropTypes.bool,
  onCountryChange: PropTypes.func,
  onEmailChange: PropTypes.func,
  onZipCodeBlur: PropTypes.func,
  selectedCountry: PropTypes.string,
  selectedState: PropTypes.string,
  nickName: PropTypes.string,
  addressType: PropTypes.string,
  showAddressType: PropTypes.bool,
  autoCompleteOnZipcode: PropTypes.bool,
  forcePhoneRequired: PropTypes.bool,
  forceLine1Required: PropTypes.bool,
  forceEmailRequired: PropTypes.bool,
  validateEmailDomains: PropTypes.bool,
  isDeepLinkFlow: PropTypes.bool,
  showNeighborhood: PropTypes.bool,
  valuefromSessionStorage: PropTypes.shape({}),
  isDealerUser: PropTypes.bool,
  profileAddressId: PropTypes.string,
  labelClassName: PropTypes.string,
  showMobileDrawerFlag: PropTypes.bool
};
export default AddressFormFields;
